import React, { useState, useEffect, useRef } from 'react'
import Dashboard from './Dashboard'
import { login, logout, getMe } from '../api'
import { useLanguage } from '../LanguageContext'

const VIDEO_WEBM = '/images/video1.mp4'
const VIDEO_MP4 = '/images/video1.mp4'

const LANG_OPTIONS = [
  { code: 'en', short: 'ENG', labelKey: 'pickerLangEnglish' },
  { code: 'fr', short: 'FRA', labelKey: 'pickerLangFrench' },
  { code: 'nl', short: 'NLD', labelKey: 'pickerLangDutch' },
]

export default function Home() {
  const { t, setLang, lang: langCode } = useLanguage()

  const [screen, setScreen] = useState('lang')
  const [langReady, setLangReady] = useState(false)
  const [langLeaving, setLangLeaving] = useState(false)
  const [loginReady, setLoginReady] = useState(false)

  const [memberId, setMemberId] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [isRevealing, setIsRevealing] = useState(false)
  const [isRevealed, setIsRevealed] = useState(false)
  const [showPassword, setShowPassword] = useState(false)  // ← add this
  const [loggedIn, setLoggedIn] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [error, setError] = useState('')


  const inactivityTimer = useRef(null)
  const blurVideoRef = useRef(null)
  const clearVideoRef = useRef(null)

  /* ── Restore session on mount ── */
  useEffect(() => {
    getMe().then(({ user }) => {
      if (user) {
        setCurrentUser(user)
        setIsRevealed(true)
        setLoggedIn(true)
      }
    })
  }, [])

  /* ── Lang screen entrance ── */
  useEffect(() => {
    const timer = setTimeout(() => setLangReady(true), 120)
    return () => clearTimeout(timer)
  }, [])

  /* ── Keep both videos in sync ── */
  useEffect(() => {
    const blurVid = blurVideoRef.current
    const clearVid = clearVideoRef.current
    if (!blurVid || !clearVid) return
    const syncTime = () => {
      if (Math.abs(blurVid.currentTime - clearVid.currentTime) > 0.3) {
        clearVid.currentTime = blurVid.currentTime
      }
    }
    blurVid.addEventListener('timeupdate', syncTime)
    return () => blurVid.removeEventListener('timeupdate', syncTime)
  }, [])

  /* ── Inactivity timer ── */
  const resetInactivityTimer = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    inactivityTimer.current = setTimeout(() => {
      handleSignOut()
    }, 15 * 60 * 1000)
  }

  useEffect(() => {
    if (!isRevealed) return
    window.addEventListener('mousemove', resetInactivityTimer)
    window.addEventListener('keydown', resetInactivityTimer)
    window.addEventListener('click', resetInactivityTimer)
    resetInactivityTimer()
    return () => {
      window.removeEventListener('mousemove', resetInactivityTimer)
      window.removeEventListener('keydown', resetInactivityTimer)
      window.removeEventListener('click', resetInactivityTimer)
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    }
  }, [isRevealed])

  /* ── Full reset ── */
  const resetToLang = () => {
    setLoggedIn(false)
    setCurrentUser(null)
    setIsRevealed(false)
    setIsRevealing(false)
    setMemberId('')
    setAccessCode('')
    setError('')
    setScreen('lang')
    setLangReady(false)
    setLangLeaving(false)
    setLoginReady(false)
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    setTimeout(() => setLangReady(true), 120)
  }

  /* ── Sign out — static import, always works ── */
  const handleSignOut = async () => {
    try {
      await logout()
    } catch (_) {
      // If server call fails the token is still cleared by logout() in api.js
    }
    resetToLang()
  }

  /* ── Language selection ── */
  const handleSelectLang = (opt) => {
    setLang(opt.code)
    setLangLeaving(true)
    setTimeout(() => {
      setScreen('login')
      setLangLeaving(false)
      setTimeout(() => setLoginReady(true), 60)
    }, 680)
  }

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault()
    if (!memberId.trim() || !accessCode.trim()) {
      setError(t('errEnterMemberId'))
      return
    }
    setError('')
    setIsRevealing(true)
    try {
      const { user } = await login(memberId, accessCode)
      setCurrentUser(user)
      setTimeout(() => {
        setIsRevealed(true)
        setIsRevealing(false)
        setTimeout(() => setLoggedIn(true), 300)
      }, 1000)
    } catch (err) {
      setIsRevealing(false)
      setError(err.status === 401
        ? t('errInvalidCredentials')
        : t('errServerUnreachable'))
    }
  }

  const clearError = () => { if (error) setError('') }

  /* ── Dashboard ── */
  if (loggedIn && currentUser) {
    return <Dashboard onSignOut={handleSignOut} user={currentUser} />
  }

  const rootClass = [
    'mw-root',
    isRevealing ? 'mw-revealing' : '',
    isRevealed ? 'mw-revealed' : '',
  ].filter(Boolean).join(' ')

  const loginLang = LANG_OPTIONS.find(l => l.code === langCode) || LANG_OPTIONS[0]

  return (
    <div className={rootClass}>

      <div className="mw-video-wrap mw-video-wrap--blur">
        <video ref={blurVideoRef} className="mw-video"
          autoPlay muted loop playsInline preload="auto" aria-hidden="true">
          <source src={VIDEO_WEBM} type="video/webm" />
          <source src={VIDEO_MP4} type="video/mp4" />
        </video>
      </div>

      <div className="mw-video-wrap mw-video-wrap--clear">
        <video ref={clearVideoRef} className="mw-video"
          autoPlay muted loop playsInline preload="auto" aria-hidden="true">
          <source src={VIDEO_WEBM} type="video/webm" />
          <source src={VIDEO_MP4} type="video/mp4" />
        </video>
      </div>

      <div className="mw-bg-overlay" aria-hidden="true" />
      <div className="mw-grain" aria-hidden="true" />

      {/* ══ LANGUAGE SELECTOR ══ */}
      {screen === 'lang' && (
        <main className="mw-stage">
          <div className={[
            'mw-lang-card',
            langReady ? 'mw-lang-card--visible' : '',
            langLeaving ? 'mw-lang-card--leaving' : '',
          ].filter(Boolean).join(' ')}>

            <div className="mw-wordmark mb-4">
              <img src="/images/logohome.png" alt={t('logoAlt')} />
            </div>
            <p className="mw-lang-prompt">{t('selectYourLanguage')}</p>
            <div className="mw-lang-options" role="list">
              {LANG_OPTIONS.map((opt, i) => (
                <button
                  key={opt.code}
                  className="mw-lang-btn"
                  style={{ animationDelay: langReady ? `${i * 0.11 + 0.18}s` : '0s' }}
                  onClick={() => handleSelectLang(opt)}
                  aria-label={t('ariaSelectLang', { label: t(opt.labelKey) })}
                  role="listitem"
                >
                  <span className="mw-lang-btn__short">{opt.short}</span>
                  <span className="mw-lang-btn__divider" aria-hidden="true" />
                  <span className="mw-lang-btn__full">{t(opt.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>
        </main>
      )}

      {/* ══ LOGIN SCREEN ══ */}
      {screen === 'login' && (
        <main className="mw-stage">
          <div className={`mw-card${loginReady ? ' mw-card--visible' : ''}`}>

            <div className="mw-wordmark mb-4">
              <img src="/images/logohome.png" alt={t('logoAlt')} />
            </div>

            <button
              className="mw-lang-back"
              onClick={() => {
                setLoginReady(false)
                setTimeout(() => {
                  setScreen('lang')
                  setTimeout(() => setLangReady(true), 60)
                }, 400)
              }}
              aria-label={t('ariaChangeLanguage')}
            >
              <span className="mw-lang-back__flag">{loginLang.short}</span>
              <span className="mw-lang-back__chevron" aria-hidden="true">‹</span>
              <span>{t('changeLanguage')}</span>
            </button>

            <form onSubmit={handleLogin} noValidate aria-label={t('formMemberLogin')} className="w-100">
              <div className="mw-field-group">
                <div className="mw-field">
                  <label className="mw-label" htmlFor="memberId">{t('memberId')}</label>
                  <input
                    id="memberId"
                    type="text"
                    className={`mw-input${error ? ' mw-input--error' : ''}`}
                    value={memberId}
                    onChange={e => { setMemberId(e.target.value); clearError() }}
                    autoComplete="off"
                    spellCheck={false}
                    aria-required="true"
                    disabled={isRevealing}
                  />
                </div>
                <div className="mw-field">
                  <label className="mw-label" htmlFor="accessCode">{t('accessCode')}</label>
                  <div className="mw-input-wrap inputwrapaccess">
                    <input
                      id="accessCode"
                      type={showPassword ? 'text' : 'password'}
                      className={`mw-input mw-input--with-icon${error ? ' mw-input--error' : ''}`}
                      value={accessCode}
                      onChange={e => { setAccessCode(e.target.value); clearError() }}
                      autoComplete="current-password"
                      aria-required="true"
                      disabled={isRevealing}
                    />
                    <button
                      type="button"
                      className="mw-input-eye"
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide access code' : 'Show access code'}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

              </div>

              {error && <p className="mw-error" role="alert">{error}</p>}

              <button type="submit" className="mw-btn" disabled={isRevealing}>
                {isRevealing ? (
                  <span className="mw-btn__loading" aria-label={t('signingIn')}>
                    <span /><span /><span />
                  </span>
                ) : t('enter')}
              </button>
            </form>
          </div>
        </main>
      )}

      <footer className="mw-footer">
        <p>
          {t('footerLine1')} <br />
          {t('footerLine2')}
        </p>
      </footer>

    </div>
  )
}